import type { FormikValues } from 'formik';
import { isEqual, mapValues, pick } from 'lodash';
import type { TestFunction } from 'yup';

import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';

import { DETAILS_TAB_KEY, FORMIK_DETAILS_FIELD_IDS } from './constants';

export const getFullFieldPath = (tabId: string, fieldId: string): string => `${tabId}.${fieldId}`;

export const getFullDetailsTabFieldPath = (fieldId: string): string => getFullFieldPath(DETAILS_TAB_KEY, fieldId);

export const getReasonValidationSchema = (
  originalContact: ContactFullModel | undefined,
  getValuesToCompare: (values: FormikValues) => FormikValues,
  getInitialValues: (contact: ContactFullModel | undefined) => FormikValues
): TestFunction<string | null | undefined> => {
  return (reason, ctx) => {
    if (reason) {
      return true;
    }

    // @ts-expect-error - `from` exists, but is not included in the type definition for TestContext in our current version of Yup
    const values = ctx.from?.[0]?.value ?? {};

    const originalValuesToCompare = getValuesToCompare(pick(getInitialValues(originalContact), Object.keys(values)));
    const updatedValuesToCompare = getValuesToCompare(values);
    return isEqual(originalValuesToCompare, updatedValuesToCompare);
  };
};

export const getInitializedValues = (values: FormikValues): FormikValues => mapValues(values, (value) => value ?? '');

// Mirror BE _address_matches_contact normalization (lower + strip)
export const normalizeOptionalString = (v: string | null | undefined): string =>
  String(v ?? '')
    .trim()
    .toLowerCase();

// Projects a fetched contact read-model down to the fields a v2 identity PATCH may legitimately
// change, so callers that mutate one field (e.g. role) don't echo the entire ~40-key read-model.
export const pickEditableIdentityFields = (contact: Record<string, unknown>): Record<string, unknown> =>
  pick(contact, Object.values(FORMIK_DETAILS_FIELD_IDS));
